import { useMemo } from 'react';
import { $, fH, fN, MarginChip, DEPT_COLORS } from './utils';

export default function DepartmentsTab({ users }) {
  const depts = useMemo(() => {
    const map = {};
    for (const u of users) {
      const d = u.department || 'Unassigned';
      if (!map[d]) map[d] = { name:d, hc:0, sal:0, fl:0, ch:0, th:0, bh:0, nbh:0, ih:0, gh:0, tc:0, gc:0, rev:0, pc:0 };
      const b = map[d];
      b.hc++; if (u.is_freelancer) b.fl++; else b.sal++;
      b.ch  += u.contracted_hours    || 0;
      b.th  += u.total_hours_worked  || 0;
      b.bh  += u.billable_hours      || 0;
      b.nbh += u.non_billable_hours  || 0;
      b.ih  += u.internal_hours      || 0;
      b.gh  += u.gap_hours           || 0;
      b.tc  += u.total_cost          || 0;
      b.gc  += u.cost_of_gap         || 0;
      b.rev += u.revenue_attributed  || 0;
      b.pc  += u.profit_contributed  || 0;
    }
    return Object.values(map).map(b => ({
      ...b,
      up: b.ch > 0 ? b.bh / b.ch * 100 : 0,
      br: b.th > 0 ? b.bh / b.th * 100 : 0,
      m:  b.rev > 0 ? b.pc / b.rev * 100 : null,
    })).sort((a,b) => b.rev - a.rev);
  }, [users]);

  const maxRev    = Math.max(...depts.map(d=>d.rev), 1);
  const maxAbsP   = Math.max(...depts.map(d=>Math.abs(d.pc)), 1);
  const profSorted = [...depts].sort((a,b)=>b.pc-a.pc);

  const tot = depts.reduce((a,d)=>({
    hc:a.hc+d.hc, ch:a.ch+d.ch, th:a.th+d.th, bh:a.bh+d.bh,
    ih:a.ih+d.ih, gh:a.gh+d.gh, tc:a.tc+d.tc, rev:a.rev+d.rev, pc:a.pc+d.pc,
  }), { hc:0,ch:0,th:0,bh:0,ih:0,gh:0,tc:0,rev:0,pc:0 });
  const totUp = tot.ch>0 ? tot.bh/tot.ch*100 : 0;
  const totBr = tot.th>0 ? tot.bh/tot.th*100 : 0;
  const totM  = tot.rev>0 ? tot.pc/tot.rev*100 : null;

  return (
    <>
      {/* Bar charts */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
        <BarCard title="Revenue by Department">
          {depts.map(d => {
            const c = DEPT_COLORS[d.name] || DEPT_COLORS['Unassigned'];
            return (
              <BarRow key={d.name} label={d.name} value={$(d.rev)} width={d.rev/maxRev*100} color={c.fg} />
            );
          })}
        </BarCard>
        <BarCard title="Profit by Department">
          {profSorted.map(d => (
            <BarRow key={d.name} label={d.name} value={$(d.pc)}
              width={Math.abs(d.pc)/maxAbsP*100}
              color={d.pc>=0?'var(--green)':'var(--red)'}
              valueColor={d.pc>=0?'var(--green)':'var(--red)'}
            />
          ))}
        </BarCard>
      </div>

      {/* Utilization cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:20 }}>
        {depts.map(d => {
          const c = DEPT_COLORS[d.name] || DEPT_COLORS['Unassigned'];
          const upColor = d.up>=70?'var(--green)':d.up>=40?'var(--amber)':'var(--red)';
          return (
            <div key={d.name} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:16 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:600, background:c.bg, color:c.fg }}>{d.name}</span>
                <span style={{ fontSize:11, color:'var(--text3)' }}>{d.hc} people</span>
              </div>
              <div style={{ marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text3)', marginBottom:3 }}>
                  <span>Utilization</span><span style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:upColor }}>{fN(d.up, 0)}%</span>
                </div>
                <div style={{ height:5, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
                  <div style={{ width:`${Math.min(d.up,100)}%`, height:'100%', background:upColor, borderRadius:3 }} />
                </div>
              </div>
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text3)', marginBottom:3 }}>
                  <span>Billable %</span><span style={{ fontFamily:'DM Mono,monospace' }}>{fN(d.br, 0)}%</span>
                </div>
                <div style={{ height:5, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
                  <div style={{ width:`${Math.min(d.br,100)}%`, height:'100%', background:'var(--blue)', borderRadius:3 }} />
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text2)', marginTop:10 }}>
                <span>{d.sal}S · {d.fl}FL</span>
                <span style={{ color: d.pc>=0?'var(--green)':'var(--red)', fontFamily:'DM Mono,monospace', fontWeight:600 }}>{$(d.pc)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ overflowX:'auto', borderRadius:12, border:'1px solid var(--border)', background:'var(--card)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--card2)' }}>
              {['#','Department','People','Contracted','Worked','Billable','Internal','Gap','Util %','Bill %','Cost','Revenue','Profit','Margin'].map(h=>(
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {depts.map((d,i) => (
              <tr key={d.name} style={{ borderTop:'1px solid var(--border)' }}>
                <td style={monoTd}>{i+1}</td>
                <td style={{ padding:'9px 14px', fontWeight:600 }}>{d.name}</td>
                <td style={monoTd}>{d.hc} <span style={{ color:'var(--text3)', fontSize:11 }}>({d.sal}S/{d.fl}F)</span></td>
                <td style={monoTd}>{fH(d.ch)}</td>
                <td style={monoTd}>{fH(d.th)}</td>
                <td style={monoTd}>{fH(d.bh)}</td>
                <td style={monoTd}>{fH(d.ih)}</td>
                <td style={{ ...monoTd, color:d.gh>40?'var(--red)':'var(--text2)' }}>{fH(d.gh)}</td>
                <td style={monoTd}>{fN(d.up, 0)}%</td>
                <td style={monoTd}>{fN(d.br, 0)}%</td>
                <td style={monoTd}>{$(d.tc)}</td>
                <td style={{ ...monoTd, color:'var(--blue)' }}>{$(d.rev)}</td>
                <td style={{ ...monoTd, fontWeight:600, color:d.pc>=0?'var(--green)':'var(--red)' }}>{$(d.pc)}</td>
                <td style={{ padding:'9px 14px' }}><MarginChip value={d.m} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop:'2px solid var(--green)', background:'var(--card2)', fontWeight:700 }}>
              <td style={monoTd}></td>
              <td style={{ padding:'9px 14px' }}>TOTAL ({depts.length})</td>
              <td style={monoTd}>{tot.hc}</td>
              <td style={monoTd}>{fH(tot.ch)}</td>
              <td style={monoTd}>{fH(tot.th)}</td>
              <td style={monoTd}>{fH(tot.bh)}</td>
              <td style={monoTd}>{fH(tot.ih)}</td>
              <td style={monoTd}>{fH(tot.gh)}</td>
              <td style={monoTd}>{fN(totUp, 0)}%</td>
              <td style={monoTd}>{fN(totBr, 0)}%</td>
              <td style={monoTd}>{$(tot.tc)}</td>
              <td style={{ ...monoTd, color:'var(--blue)' }}>{$(tot.rev)}</td>
              <td style={{ ...monoTd, color:tot.pc>=0?'var(--green)':'var(--red)' }}>{$(tot.pc)}</td>
              <td style={{ padding:'9px 14px' }}><MarginChip value={totM} /></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

function BarCard({ title, children }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
      <h4 style={{ fontSize:13, fontWeight:700, marginBottom:12, color:'var(--text2)' }}>{title}</h4>
      {children}
    </div>
  );
}

function BarRow({ label, value, width, color, valueColor }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, fontSize:12 }}>
      <div style={{ width:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:500 }}>{label}</div>
      <div style={{ flex:1, height:8, background:'var(--card2)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${width}%`, height:'100%', borderRadius:4, background:color }} />
      </div>
      <div style={{ width:80, textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:11, color:valueColor||'var(--text2)' }}>{value}</div>
    </div>
  );
}

const thStyle = { padding:'11px 14px', textAlign:'left', fontWeight:600, fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'.7px', color:'var(--text2)', whiteSpace:'nowrap', position:'sticky', top:0, background:'var(--card2)' };
const monoTd = { padding:'9px 14px', fontFamily:'DM Mono,monospace', fontSize:12, whiteSpace:'nowrap' };
